// Serviço para comunicação com endpoints de posts
import apiService from "../shared/apiService";

export interface AudioTrack {
  id: string;
  title: string;
  artist: string;
  duration_sec: number | null;
  preview_url: string | null;
  cover_art_url: string | null;
}
import { API_ENDPOINTS } from "../shared/constants";
import { postsCache } from "../utils/smartCache";

class PostsService {
  // ===== MÉTODOS PARA POSTS COM CACHE =====

  /**
   * Função auxiliar para extrair posts e metadados da resposta da API
   */
  private extractPostsFromResponse(response: any): {
    posts: any[];
    totalCount: number;
  } {
    let posts: any[] = [];
    let totalCount = 0;

    if (Array.isArray(response)) {
      posts = response;
      totalCount = response.length;
    } else if (response && typeof response === "object") {
      posts =
        response.posts ||
        response.data ||
        (response.success && response.posts) ||
        [];
      totalCount =
        response.total_count ??
        response.total ??
        response.totalCount ??
        response.total_items ??
        posts.length;
    }

    return { posts, totalCount };
  }

  /**
   * Busca posts usando a rota consolidada com parâmetros de filtro e paginação
   */
  private async getPostsByStatus(
    status: string,
    forceRefresh: boolean = false,
    page: number = 1,
    limit: number = 10,
    search?: string,
  ): Promise<{ posts: any[]; totalCount: number }> {
    const cacheMethods: Record<
      string,
      (
        fetcher: () => Promise<{ posts: any[]; totalCount: number }>,
        forceRefresh?: boolean,
        page?: number,
        limit?: number,
        search?: string,
      ) => Promise<{ posts: any[]; totalCount: number }>
    > = {
      pending: postsCache.getPendingPosts.bind(postsCache),
      approved: postsCache.getApprovedPosts.bind(postsCache),
      published: postsCache.getPublishedPosts.bind(postsCache),
      rejected: postsCache.getRejectedPosts.bind(postsCache),
      scheduled: postsCache.getScheduledPosts.bind(postsCache),
    };

    const fetcher = async () => {
      const offset = (page - 1) * limit;
      let url = `${API_ENDPOINTS.POSTS.LIST}?status=${status}&limit=${limit}&offset=${offset}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      const response = await apiService.get(url);
      return this.extractPostsFromResponse(response);
    };

    const cacheMethod = cacheMethods[status];
    if (cacheMethod && !search) {
      return cacheMethod(fetcher, forceRefresh, page, limit);
    } else {
      return fetcher();
    }
  }

  async getPendingPosts(
    forceRefresh: boolean = false,
    page: number = 1,
    limit: number = 10,
    search?: string,
  ): Promise<{ posts: any[]; totalCount: number }> {
    try {
      const result = await this.getPostsByStatus(
        "pending",
        forceRefresh,
        page,
        limit,
        search,
      );
      console.log(
        "📋 [POSTS SERVICE] getPendingPosts retornando",
        result?.posts?.length || 0,
        "de",
        result?.totalCount || 0,
        "posts",
      );
      return result || { posts: [], totalCount: 0 };
    } catch (error: any) {
      console.error(
        "❌ [POSTS SERVICE] Erro ao buscar posts pendentes:",
        error,
      );
      return { posts: [], totalCount: 0 };
    }
  }

  async getApprovedPosts(
    forceRefresh: boolean = false,
    page: number = 1,
    limit: number = 10,
    search?: string,
  ): Promise<{ posts: any[]; totalCount: number }> {
    return this.getPostsByStatus("approved", forceRefresh, page, limit, search);
  }

  async getPublishedPosts(
    forceRefresh: boolean = false,
    page: number = 1,
    limit: number = 10,
    search?: string,
  ): Promise<{ posts: any[]; totalCount: number }> {
    return this.getPostsByStatus(
      "published",
      forceRefresh,
      page,
      limit,
      search,
    );
  }

  async getRejectedPosts(
    page: number = 1,
    limit: number = 10,
    search?: string,
  ): Promise<{ posts: any[]; totalCount: number }> {
    return this.getPostsByStatus("rejected", false, page, limit, search);
  }

  async getScheduledPosts(
    page: number = 1,
    limit: number = 10,
    search?: string,
  ): Promise<{ posts: any[]; totalCount: number }> {
    // CORREÇÃO: Usar o endpoint dedicado que agrupa agendados + falhos
    try {
      const offset = (page - 1) * limit;
      let url = `${API_ENDPOINTS.POSTS.SCHEDULED}?limit=${limit}&offset=${offset}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      const response = await apiService.get(url);
      return this.extractPostsFromResponse(response);
    } catch (error) {
      console.error("❌ Erro ao buscar posts agendados e falhos:", error);
      // Fallback para o comportamento antigo se o novo endpoint falhar
      return this.getPostsByStatus("scheduled", false, page, limit, search);
    }
  }

  /**
   * Busca posts de todos os status (endpoint consolidado sem filtro de status,
   * já paginado corretamente pelo backend).
   */
  async getAllPosts(
    forceRefresh: boolean = false,
    page: number = 1,
    limit: number = 10,
    search?: string,
  ): Promise<{ posts: any[]; totalCount: number }> {
    const fetcher = async () => {
      const offset = (page - 1) * limit;
      let url = `${API_ENDPOINTS.POSTS.LIST}?limit=${limit}&offset=${offset}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      const response = await apiService.get(url);
      return this.extractPostsFromResponse(response);
    };

    if (search) {
      return fetcher();
    }
    return postsCache.getAllPosts(fetcher, forceRefresh, page, limit);
  }

  /**
   * Contagem agregada de posts por status, agrupados por plataforma e autoria
   * (IA/humano) — feita no banco (COUNT/GROUP BY), sem carregar os posts
   * completos. Usada para popular os contadores dos filtros da UI.
   */
  async getPostCounts(
    status: string,
    search?: string,
  ): Promise<{
    total: number;
    groups: { platform: string; ai_generated: boolean; count: number }[];
  }> {
    try {
      let url = `${API_ENDPOINTS.POSTS.COUNTS}?status=${encodeURIComponent(status)}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      const response = await apiService.get(url);
      return {
        total: response?.total || 0,
        groups: response?.groups || [],
      };
    } catch (error) {
      console.error(
        "❌ [POSTS SERVICE] Erro ao buscar contagens agregadas:",
        error,
      );
      return { total: 0, groups: [] };
    }
  }

  async getStats(): Promise<any> {
    return postsCache.getStats(async () => {
      const response = await apiService.get(API_ENDPOINTS.POSTS.STATS);

      // Lidar com diferentes formatos de resposta
      if (response && typeof response === "object") {
        if (response.stats) {
          return response.stats;
        } else if (response.data) {
          return response.data;
        }
        // Se a resposta já é o objeto de stats
        if (response.pending !== undefined || response.approved !== undefined) {
          return response;
        }
      }

      return {};
    });
  }

  async createPost(postData: any): Promise<any> {
    const result = await apiService.post(API_ENDPOINTS.POSTS.CREATE, postData);
    // Invalidar cache após criar post
    postsCache.invalidateAllPosts();
    return result;
  }

  invalidateAllPosts(): void {
    postsCache.invalidateAllPosts();
  }

  async uploadImage(postId: number, file: File): Promise<any> {
    const data = await apiService.uploadFile(
      API_ENDPOINTS.POSTS_EXTENDED.UPLOAD_IMAGE.replace("{id}", String(postId)),
      file,
    );
    postsCache.invalidatePostById(postId);
    return data;
  }

  async uploadVideo(
    postId: number,
    file: File,
    destination: string,
    caption?: string,
    hashtags?: string[],
  ): Promise<any> {
    const additionalData: Record<string, any> = {
      destination,
    };
    if (caption) additionalData.caption = caption;
    if (hashtags && hashtags.length)
      additionalData.hashtags = JSON.stringify(hashtags);

    const data = await apiService.uploadFile(
      API_ENDPOINTS.POSTS.UPLOAD_VIDEO.replace("{id}", String(postId)),
      file,
      additionalData,
    );

    // Invalidar cache de todos os posts para garantir que a lista seja recarregada
    postsCache.invalidateAllPosts();
    console.log(
      "📹 Upload de vídeo concluído, cache invalidado. Resposta:",
      data,
    );
    return data;
  }

  async publishVideo(postId: number): Promise<any> {
    const data = await apiService.post(
      API_ENDPOINTS.POSTS.PUBLISH_VIDEO.replace("{id}", String(postId)),
    );
    postsCache.invalidatePostById(postId);
    return data;
  }

  /**
   * Remove image_info de um post quando o arquivo físico não existe.
   * @param postId - ID do post
   * @returns Promise com resultado da operação
   */
  async clearImage(postId: number): Promise<any> {
    const data = await apiService.delete(`/posts/${postId}/clear-image`);
    postsCache.invalidatePostById(postId);
    return data;
  }

  /**
   * Remove video_info de um post quando o arquivo físico não existe.
   * @param postId - ID do post
   * @returns Promise com resultado da operação
   */
  async clearVideo(postId: number): Promise<any> {
    const data = await apiService.delete(`/posts/${postId}/clear-video`);
    postsCache.invalidatePostById(postId);
    return data;
  }

  async publishMultiPlatform(
    postId: number,
    platforms: string[],
    audioConfig?: {
      audio_id: string;
      audio_volume?: number;
      video_volume?: number;
    },
  ): Promise<any> {
    const payload: Record<string, unknown> = { platforms };
    if (audioConfig?.audio_id) {
      payload.audio_id = audioConfig.audio_id;
      payload.audio_volume = audioConfig.audio_volume ?? 100;
      payload.video_volume = audioConfig.video_volume ?? 50;
    }
    const data = await apiService.post(
      API_ENDPOINTS.POSTS.PUBLISH_MULTI.replace("{id}", String(postId)),
      payload,
    );
    postsCache.invalidatePostById(postId);
    return data;
  }

  async searchInstagramAudio(
    query?: string,
    limit = 25,
  ): Promise<{
    success: boolean;
    data: AudioTrack[];
    total: number;
    trending: boolean;
  }> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (query?.trim()) params.set("q", query.trim());
    return apiService.get(`/instagram/audio/search?${params.toString()}`);
  }

  async publishPost(postId: number): Promise<any> {
    // Usar rota consolidada de status com action="approve"
    const result = await apiService.post(
      API_ENDPOINTS.POSTS.STATUS.replace("{id}", String(postId)),
      { action: "approve" },
    );
    // Invalidar cache após aprovar post
    postsCache.invalidateAllPosts();
    return result;
  }

  async getPostById(postId: number): Promise<any> {
    const response = await apiService.get(`/posts/${postId}`);
    return response;
  }

  async editPost(postId: number, data: any): Promise<any> {
    const result = await apiService.put(
      API_ENDPOINTS.POSTS.EDIT.replace("{id}", String(postId)),
      data,
    );
    // Invalidar cache após editar post
    postsCache.invalidatePostById(postId);
    return result;
  }

  async schedulePost(postId: number, scheduleData: any): Promise<any> {
    // Converter date/time para scheduled_date (datetime ISO string) se necessário
    let finalScheduleData = scheduleData;
    if (scheduleData.date && scheduleData.time) {
      // Combinar date e time em um datetime ISO string
      const scheduledDateTime = new Date(
        `${scheduleData.date}T${scheduleData.time}:00`,
      );
      finalScheduleData = {
        scheduled_date: scheduledDateTime.toISOString(),
      };
    } else if (!scheduleData.scheduled_date && scheduleData.date) {
      // Se só tem date, usar meia-noite
      const scheduledDateTime = new Date(`${scheduleData.date}T00:00:00`);
      finalScheduleData = {
        scheduled_date: scheduledDateTime.toISOString(),
      };
    }

    const result = await apiService.post(
      API_ENDPOINTS.POSTS.SCHEDULE.replace("{id}", String(postId)),
      finalScheduleData,
    );
    // Invalidar cache após agendar post
    postsCache.invalidatePostById(postId);
    return result;
  }

  async reschedulePostPublished(
    postId: number,
    scheduleData: any,
  ): Promise<any> {
    // Combinar date e time em um datetime ISO string
    const scheduledDateTime = new Date(
      `${scheduleData.date}T${scheduleData.time}:00`,
    );

    const result = await apiService.post(
      API_ENDPOINTS.POSTS.RESCHEDULE.replace("{id}", String(postId)),
      {
        scheduled_date: scheduledDateTime.toISOString(),
      },
    );

    // Invalidar cache após reagendar post
    postsCache.invalidateAllPosts();
    return result;
  }

  async deletePost(postId: number): Promise<any> {
    const result = await apiService.delete(
      API_ENDPOINTS.POSTS.DELETE.replace("{id}", String(postId)),
    );
    // Invalidar cache após deletar post
    postsCache.invalidatePostById(postId);
    return result;
  }

  async duplicatePost(postId: number, targetPlatform?: string): Promise<any> {
    const result = await apiService.post(`/posts/${postId}/duplicate`, {
      target_platform: targetPlatform,
    });
    // Invalidar cache após duplicar post
    postsCache.invalidateAllPosts();
    return result;
  }

  async rejectPost(postId: number, reason: string): Promise<any> {
    // Usar rota consolidada de status com action="reject"
    const result = await apiService.post(
      API_ENDPOINTS.POSTS.STATUS.replace("{id}", String(postId)),
      { action: "reject", reason },
    );
    // Invalidar cache após rejeitar post
    postsCache.invalidatePostById(postId);
    return result;
  }

  async revertToPending(postId: number): Promise<any> {
    // Usar rota consolidada de status com action="revert_to_pending"
    const result = await apiService.post(
      API_ENDPOINTS.POSTS.STATUS.replace("{id}", String(postId)),
      { action: "revert_to_pending" },
    );
    // Invalidar cache após reverter post
    postsCache.invalidatePostById(postId);
    return result;
  }

  async unschedulePost(postId: number): Promise<any> {
    // Usar rota consolidada de status com action="unschedule"
    const result = await apiService.post(
      API_ENDPOINTS.POSTS.STATUS.replace("{id}", String(postId)),
      { action: "unschedule" },
    );
    // Invalidar cache após remover agendamento
    postsCache.invalidatePostById(postId);
    postsCache.invalidateAllPosts(); // Limpa tudo para garantir atualização da lista de cronograma
    return result;
  }

  async enhancePost(postId: number): Promise<any> {
    try {
      const result = await apiService.post(
        API_ENDPOINTS.POSTS.ENHANCE.replace("{id}", String(postId)),
      );
      // Invalidar cache após melhorar post
      postsCache.invalidatePostById(postId);
      return result;
    } catch (error) {
      // Notificar erro se houver
      console.error("Erro ao melhorar post:", error);
      throw error;
    }
  }

  async republishPost(
    postId: number,
    usePersonalNetwork: boolean = true,
  ): Promise<any> {
    // Converter booleano para linkedin_type string
    const linkedin_type = usePersonalNetwork ? "personal" : "organization";
    const result = await apiService.post(
      API_ENDPOINTS.POSTS.REPUBLISH.replace("{id}", String(postId)),
      {
        linkedin_type: linkedin_type,
      },
    );

    // Limpar cache se necessário
    postsCache.invalidateAllPosts();
    return result;
  }

  /**
   * Descarta uma falha de postagem (muda status para rejected)
   */
  async dismissPostFailure(postId: number): Promise<any> {
    const result = await apiService.post(
      API_ENDPOINTS.POSTS.DISMISS_FAILURE.replace("{id}", String(postId)),
      {},
    );

    // Limpar cache para refletir a remoção da lista de agendados/falhos
    postsCache.invalidateAllPosts();
    return result;
  }

  // ===== MÉTODOS PARA CONTEÚDO =====

  async getContentLibrary(
    page: number = 1,
    limit: number = 50,
  ): Promise<{
    contents: any[];
    total: number;
    counts_by_type?: {
      url: number;
      text: number;
      prompt: number;
      file: number;
    };
  }> {
    try {
      const offset = (page - 1) * limit;
      const response = await apiService.get(API_ENDPOINTS.CONTENT.LIBRARY, {
        limit,
        offset,
      });

      // A API retorna {success: true, contents: [...], total: n}
      if (response && response.success) {
        return {
          contents: response.contents || [],
          total: response.total || 0,
          counts_by_type: response.counts_by_type,
        };
      }

      // Fallback para quando o formato é inesperado mas é um array
      if (Array.isArray(response)) {
        return { contents: response, total: response.length };
      }

      return {
        contents: response?.contents || [],
        total: response?.total || 0,
      };
    } catch (error: any) {
      console.error("❌ Erro ao buscar biblioteca de conteúdo:", error);
      throw error;
    }
  }

  async deleteContent(contentId: number): Promise<any> {
    return await apiService.delete(
      API_ENDPOINTS.CONTENT.LIBRARY_ITEM.replace("{id}", String(contentId)),
    );
  }

  async getScheduleOverview(): Promise<any> {
    return await apiService.get(API_ENDPOINTS.CONTENT.SCHEDULE_OVERVIEW);
  }

  async optimizeSchedule(data: any): Promise<any> {
    return await apiService.post(API_ENDPOINTS.CONTENT.OPTIMIZE_SCHEDULE, data);
  }

  async editContent(contentId: number, data: any): Promise<any> {
    // O endpoint espera title e content, mapear se necessário
    const editData = {
      title: data.name || data.title,
      content: data.original_data || data.content,
      content_type: data.type || data.content_type,
      business_type: data.business_type,
      platforms: data.platforms || [], // Incluir plataformas
    };
    return await apiService.put(
      API_ENDPOINTS.CONTENT.LIBRARY_ITEM.replace("{id}", String(contentId)),
      editData,
    );
  }

  async suggestHooks(
    postId: number,
    format: string,
    templateStyle: string,
  ): Promise<{
    success: boolean;
    error?: string;
    suggestions: { headers: string[]; bodies: string[]; ctas: string[] };
  }> {
    return apiService.post(`/posts/${postId}/suggest-hooks`, {
      format,
      template_style: templateStyle,
    });
  }

  async generateCreative(
    postId: number,
    params: {
      header: string;
      body: string;
      cta: string;
      format: string;
      template_style: string;
      background_prompt?: string;
      provider?: string;
      carousel_count?: number;
      header_highlight?: string;
      reuse_background_url?: string;
    },
  ): Promise<any> {
    return apiService.post(`/posts/${postId}/generate-creative`, params, {
      timeout: 60000,
    });
  }

  async getCreativeGenerationStatus(
    postId: number,
    taskId: string,
  ): Promise<{
    success?: boolean;
    status?: string;
    task_id?: string;
    error?: string;
    message?: string;
    image_info?: Record<string, unknown>;
    dimensions?: string;
    post?: Record<string, unknown>;
  }> {
    return apiService.get(
      `/posts/${postId}/generate-creative/status/${taskId}`,
    );
  }

  async generateCreativeAndWait(
    postId: number,
    params: {
      header: string;
      body: string;
      cta: string;
      format: string;
      template_style: string;
      background_prompt?: string;
      provider?: string;
      carousel_count?: number;
      header_highlight?: string;
      reuse_background_url?: string;
    },
    options?: {
      onStatus?: (status: string) => void;
    },
  ): Promise<Record<string, unknown>> {
    const enqueueRes = await this.generateCreative(postId, params);
    const jobId = enqueueRes?.job_id || enqueueRes?.task_id;

    if (!jobId) {
      if (enqueueRes?.image_info) {
        postsCache.invalidatePostById(postId);
      }
      return enqueueRes;
    }

    const result = await this.pollMediaGenerationJob(
      postId,
      jobId,
      "creative",
      {
        onStatus: options?.onStatus,
      },
    );
    postsCache.invalidatePostById(postId);
    return result;
  }

  // ===== CREATIVE AI (briefing simples: estratégia -> copy -> criativo -> revisor) =====

  async generateFromBrief(payload: {
    topic: string;
    product?: string;
    objective?: string;
    platform?: string;
    format?: string;
  }): Promise<{
    success: boolean;
    error?: string;
    post_id?: number;
    image_job_id?: string;
    copy?: Record<string, unknown>;
    icp_used?: Record<string, unknown> | null;
    review_flags?: string[];
    errors?: string[];
  }> {
    return apiService.post("/api/creative/brief", payload, {
      timeout: 60000,
    });
  }

  async generateBriefVariations(payload: {
    topic: string;
    product?: string;
    objective?: string;
    platform?: string;
    format?: string;
    num_variations?: number;
  }): Promise<{
    success: boolean;
    error?: string;
    creative_concept_id?: string;
    variations?: Array<{
      post_id?: number;
      image_job_id?: string;
      emotional_trigger?: string | null;
      copy?: Record<string, unknown>;
      review_flags?: string[];
      errors?: string[];
    }>;
  }> {
    return apiService.post("/api/creative/brief/variations", payload, {
      timeout: 180000,
    });
  }

  async getEnhanceImageStatus(
    postId: number,
    jobId: string,
  ): Promise<{
    success?: boolean;
    status?: string;
    job_id?: string;
    error?: string;
    message?: string;
    image_info?: Record<string, unknown>;
    post?: Record<string, unknown>;
    improvements?: string[];
    details?: Record<string, unknown>;
  }> {
    return apiService.get(`/posts/${postId}/enhance-image/status/${jobId}`);
  }

  async getGlobalMediaJobStatus(jobId: string): Promise<{
    success?: boolean;
    status?: string;
    job_id?: string;
    error?: string;
    message?: string;
    video_info?: Record<string, unknown>;
    image_info?: Record<string, unknown>;
    post?: Record<string, unknown>;
    details?: Record<string, unknown>;
  }> {
    return apiService.get(
      API_ENDPOINTS.POSTS_EXTENDED.MEDIA_JOB_STATUS.replace("{jobId}", jobId),
    );
  }

  async getVideoGenerationStatus(
    postId: number,
    jobId: string,
  ): Promise<{
    success?: boolean;
    status?: string;
    job_id?: string;
    error?: string;
    message?: string;
    video_info?: Record<string, unknown>;
    image_info?: Record<string, unknown>;
    post?: Record<string, unknown>;
    details?: Record<string, unknown>;
  }> {
    return apiService.get(`/posts/${postId}/generate-video/status/${jobId}`);
  }

  async getImageGenerationStatus(
    postId: number,
    jobId: string,
  ): Promise<{
    success?: boolean;
    status?: string;
    job_id?: string;
    error?: string;
    message?: string;
    image_info?: Record<string, unknown>;
    post?: Record<string, unknown>;
    details?: Record<string, unknown>;
  }> {
    return apiService.get(`/posts/${postId}/generate-image/status/${jobId}`);
  }

  async pollMediaGenerationJob(
    postId: number,
    jobId: string,
    mediaType: "image" | "video" | "creative" | "enhance",
    options?: {
      onStatus?: (status: string) => void;
      maxAttempts?: number;
      intervalMs?: number;
    },
  ): Promise<Record<string, unknown>> {
    const maxAttempts = options?.maxAttempts ?? 200;
    const intervalMs = options?.intervalMs ?? 3000;
    const fetchStatus =
      mediaType === "video"
        ? async (id: string) => {
            try {
              return await this.getVideoGenerationStatus(postId, id);
            } catch (error: any) {
              if (error?.statusCode === 404) {
                return this.getGlobalMediaJobStatus(id);
              }
              throw error;
            }
          }
        : mediaType === "creative"
          ? (id: string) => this.getCreativeGenerationStatus(postId, id)
          : mediaType === "enhance"
            ? (id: string) => this.getEnhanceImageStatus(postId, id)
            : (id: string) => this.getImageGenerationStatus(postId, id);

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const statusRes = await fetchStatus(jobId);
      const status = statusRes?.status;
      options?.onStatus?.(status || "pending");

      if (status === "completed") {
        return statusRes;
      }

      if (status === "failed") {
        const err = new Error(
          statusRes?.error ||
            statusRes?.message ||
            `Erro ao gerar ${mediaType}`,
        ) as Error & { statusCode?: number; errorData?: typeof statusRes };
        err.statusCode = 500;
        err.errorData = statusRes;
        throw err;
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    throw new Error(
      `Tempo esgotado aguardando a geração de ${
        mediaType === "image"
          ? "imagem"
          : mediaType === "enhance"
            ? "melhoria de imagem"
            : mediaType === "creative"
              ? "criativo"
              : "vídeo"
      }.`,
    );
  }

  async enhanceImageAndWait(
    postId: number,
    body: { restrictions?: string; provider?: string },
    options?: { onStatus?: (status: string) => void },
  ): Promise<Record<string, unknown>> {
    const enqueueRes = await apiService.post(
      API_ENDPOINTS.POSTS_EXTENDED.ENHANCE_IMAGE.replace(
        "{id}",
        String(postId),
      ),
      body,
      { timeout: 60000 },
    );
    const jobId = enqueueRes?.job_id || enqueueRes?.task_id;
    if (!jobId) {
      return enqueueRes;
    }
    const result = await this.pollMediaGenerationJob(postId, jobId, "enhance", {
      onStatus: options?.onStatus,
    });
    postsCache.invalidatePostById(postId);
    return result;
  }

  async generateImageAndWait(
    postId: number,
    body: Record<string, unknown> | FormData,
    options?: { timeout?: number },
  ): Promise<Record<string, unknown>> {
    const enqueueRes = await apiService.post(
      `/posts/${postId}/generate-image`,
      body,
      { timeout: options?.timeout ?? 60000 },
    );
    const jobId = enqueueRes?.job_id;
    if (!jobId) {
      return enqueueRes;
    }
    return this.pollMediaGenerationJob(postId, jobId, "image");
  }

  async generateVideoAndWait(
    postId: number,
    body: Record<string, unknown> | FormData,
    options?: { timeout?: number },
  ): Promise<Record<string, unknown>> {
    const enqueueRes = await apiService.post(
      `/posts/${postId}/generate-video`,
      body,
      { timeout: options?.timeout ?? 60000 },
    );
    const jobId = enqueueRes?.job_id || enqueueRes?.task_id;
    if (!jobId) {
      return enqueueRes;
    }
    return this.pollMediaGenerationJob(postId, jobId, "video");
  }
}

export const postsService = new PostsService();
export default postsService;
